import { PadScenario } from "store/features/app/appSlice";

// Simplified types for filtered sticks (only required fields for Rust backend)
export type SimpleStick = {
  id: string;
  survey: Array<{ x: number; y: number; z: number }>;
};

export type SimplePadScenario = Omit<PadScenario, "sticks"> & {
  sticks?: SimpleStick[];
};

/**
 * Utility function to check if two line segments intersect
 * Uses parametric line intersection algorithm
 * @param x1 First line start x coordinate
 * @param y1 First line start y coordinate
 * @param x2 First line end x coordinate
 * @param y2 First line end y coordinate
 * @param x3 Second line start x coordinate
 * @param y3 Second line start y coordinate
 * @param x4 Second line end x coordinate
 * @param y4 Second line end y coordinate
 * @returns true if the line segments intersect, false otherwise
 */
export function doLinesIntersect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number
): boolean {
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-10) return false; // Lines are parallel

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

/**
 * Function to check if a stick (wellbore path) intercepts the line
 * @param survey Array of survey points representing the wellbore path
 * @param lineStart Start point of the line
 * @param lineEnd End point of the line
 * @returns true if any segment of the stick intersects with the line
 */
export function doesStickInterceptLine(
  survey: Array<{ x: number; y: number; z: number }>,
  lineStart: { x: number; y: number },
  lineEnd: { x: number; y: number }
): boolean {
  if (!survey || survey.length < 2) return false;

  // Check each segment of the stick against the line
  for (let i = 0; i < survey.length - 1; i++) {
    const p1 = survey[i];
    const p2 = survey[i + 1];

    if (
      doLinesIntersect(
        lineStart.x,
        lineStart.y,
        lineEnd.x,
        lineEnd.y,
        p1.x,
        p1.y,
        p2.x,
        p2.y
      )
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Function to filter sticks that intercept the line and strip unnecessary fields
 * @param padScenario Original pad scenario with full stick objects
 * @param lineStart Start point of the line
 * @param lineEnd End point of the line
 * @returns Filtered pad scenario with only intercepting sticks and required fields (id, survey)
 */
export function filterInterceptingSticks(
  padScenario: PadScenario,
  lineStart: { x: number; y: number },
  lineEnd: { x: number; y: number }
): SimplePadScenario {
  if (!padScenario?.sticks?.length) return padScenario;

  const filteredSticks: SimpleStick[] = padScenario.sticks
    .filter((stick) => doesStickInterceptLine(stick.survey, lineStart, lineEnd))
    .map((stick) => ({
      id: stick.id,
      survey: stick.survey
    }));

  return {
    ...padScenario,
    sticks: filteredSticks
  };
}
